import { Router } from '@angular/router';
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Observable, of, pipe, map, switchMap, tap, catchError, filter, forkJoin, take } from 'rxjs';
import { errorHappened } from '../profile/profile.actions';
import { Store, Action } from '@ngrx/store';
import { State } from '..';
import { AlertService } from 'src/app/shared/services/alert.service';
import { downloadFile } from 'src/app/helpers/utils';
import { getAssignedEquipmentList, getAssignedEquipmentListSuccess, createEquipment,
createEquipmentSuccess, createEquipmentFail, getMyEquipmentList, getMyEquipmentListSuccess,
getMyEquipmentListFail, searchEquipment, searchEquipmentSuccess, searchEquipmentFail,
saveAssignEquipment, saveAssignEquipmentSuccess, saveAssignEquipmentFail, getEquipmentAssignPdf, getEquipmentAssignPdfSuccess, saveReturnEquipment, saveReturnEquipmentSuccess, saveReturnEquipmentFail,
loadUserEquipments, loadUserEquipmentsSuccess, loadUserEquipmentsFail, signEquipmentAssign, signEquipmentAssignSuccess, signEquipmentAssignFail, deleteEquipment, deleteEquipmentSuccess, deleteEquipmentFail, unarchiveEquipment,
  unarchiveEquipmentSuccess, unarchiveEquipmentFail, loadCampuses, loadCampusesSuccess, loadClassCodes, loadClassCodesSuccess, loadClassifications, loadClassificationsSuccess,
  saveEditAssignEquipment, saveEditAssignEquipmentSuccess, saveEditAssignEquipmentFail, getAssignedEquipments, getAssignedEquipmentsSuccess, loadEquipmentPermissionModalOptions, loadEquipmentPermissionModalOptionsSuccess, loadEquipmentPermissions, loadEquipmentPermissionsSuccess, saveEquipmentPermissions, saveEquipmentPermissionsSuccess, getEquipmentTypes, getEquipmentTypesSuccess, loadManageAssetLocations, loadManageAssetLocationsSuccess, loadManageAssetTypes, loadManageAssetTypesSuccess
} from './equipment.actions';
import { EquipmentService } from '../../services/equipment.service';
import { AssignedEquipmentModel, EquipmentModel, EquipmentWithAssignmentsModel } from '../../../models/equipmentsModel';
import { ManageAssetService } from '../../services/manage-asset.service';
import { ManageAssetAssignmentSummaryModel, ManageAssetAssignmentsRequestModel, ManageAssetSearchResultModel } from '../../../models/manageAssetModels';

@Injectable()
export class EquipmentEffects {
  constructor(private actions$: Actions,
              private equipmentService: EquipmentService,
              private manageAssetService: ManageAssetService,
              private store: Store<State>,
              private alertService: AlertService,
              private router: Router) {

  }

  loadCampuses = createEffect(() => this.actions$.pipe(
    ofType(loadCampuses),
    switchMap(action => this.equipmentService.getAllCampuses().pipe(
      map(campuses => loadCampusesSuccess({ campuses })),
      catchError(err => {
        return of(errorHappened({ err }));
      })
    ))));

  loadManageAssetTypes = createEffect(() => this.actions$.pipe(
    ofType(loadManageAssetTypes),
    switchMap(() => this.manageAssetService.getAssetTypes().pipe(
      map(assetTypes => loadManageAssetTypesSuccess({ assetTypes })),
      catchError(err => of(errorHappened({ err })))
    ))));

  loadManageAssetLocations = createEffect(() => this.actions$.pipe(
    ofType(loadManageAssetLocations),
    switchMap(() => this.manageAssetService.getAssetLocations().pipe(
      map(assetLocations => loadManageAssetLocationsSuccess({ assetLocations })),
      catchError(err => of(errorHappened({ err })))
    ))));

  loadClassCodes = createEffect(() => this.actions$.pipe(
    ofType(loadClassCodes),
    switchMap(action => this.equipmentService.getAllClassCodes().pipe(
      map(classCode => loadClassCodesSuccess({ classCode })),
      catchError(err => {
        return of(errorHappened({ err }));
      })
    ))));

  loadClassifications = createEffect(() => this.actions$.pipe(
    ofType(loadClassifications),
    switchMap(action => this.equipmentService.getAllClassifications().pipe(
      map(classification => loadClassificationsSuccess({ classification })),
      catchError(err => {
        return of(errorHappened({ err }));
      })
    ))));
    
  getEquipmentTypes = createEffect(() => this.actions$.pipe(
    ofType(getEquipmentTypes),
    switchMap(action => this.equipmentService.getEquipmentTypes().pipe(
      map(equipmentTypes => getEquipmentTypesSuccess({ equipmentTypes: equipmentTypes })),
      catchError(err => {
        return of(errorHappened({ err }));
      })
    ))))

  loadEquipmentPermissionModalOptions = createEffect(() => this.actions$.pipe(
  ofType(loadEquipmentPermissionModalOptions),
  switchMap(action => this.equipmentService.loadEquipmentPermissionModalOptions().pipe(
    map(equipmentPermissionOptions => loadEquipmentPermissionModalOptionsSuccess({ equipmentPermissionOptions })),
    catchError(err => {
      return of(errorHappened({ err }));
    })
  ))));

  loadEquipmentPermissions = createEffect(() => this.actions$.pipe(
  ofType(loadEquipmentPermissions),
  switchMap(action => this.equipmentService.loadEquipmentPermissions(action.userId, action.roleId).pipe(
    map(equipmentPermissions => loadEquipmentPermissionsSuccess({ equipmentPermissions })),
    catchError(err => {
      return of(errorHappened({ err }));
    })
  ))));

  saveEquipmentPermissions = createEffect(() => this.actions$.pipe(
    ofType(saveEquipmentPermissions),
    switchMap(action => this.equipmentService.saveEquipmentPermissions(action.equipmentPermissions).pipe(
      map(resp => saveEquipmentPermissionsSuccess({ resp })),
      catchError(err => {
        return of(errorHappened({ err }));
      })
    ))));

    saveEquipmentPermissionsSuccess = createEffect(() => this.actions$.pipe(
    ofType(saveEquipmentPermissionsSuccess),
    tap(action => {
      this.store.dispatch(loadEquipmentPermissionsSuccess({equipmentPermissions: action.resp}));
      this.alertService.success('Permissions Saved');
    })
  ), { dispatch: false });

  createEquipment = createEffect(() => this.actions$.pipe(
    ofType(createEquipment),
    switchMap(action => {
      let observable: Observable<EquipmentModel>;

      if (action.equipment.id > 0) {
        observable = this.equipmentService.editEquipment(action.equipment);
      }
      else {
        observable = this.equipmentService.createEquipment(action.equipment);
      }

      return observable.pipe(
        map(equipment => createEquipmentSuccess({ equipment })),
        catchError(err => {
          this.store.dispatch(createEquipmentFail({ err }));
          return of(errorHappened({ err }));
        }));
    })
  ));

  createEquipmentSuccess = createEffect(() => this.actions$.pipe(
    ofType(createEquipmentSuccess),
    tap(action => {
      this.alertService.success('Equipment Saved');
    })
  ), { dispatch: false });

  deleteEquipment = createEffect(() => this.actions$.pipe(
    ofType(deleteEquipment),
    switchMap(action => {
      return this.equipmentService.deleteEquipment(action.equipment).pipe(
        map(equipment => deleteEquipmentSuccess()),
        catchError(err => {
          this.store.dispatch(deleteEquipmentFail({ err }));
          return of(errorHappened({ err }));
        }));
    })
  ));

  deleteEquipmentSuccess = createEffect(() => this.actions$.pipe(
    ofType(deleteEquipmentSuccess),
    tap(action => {
      this.alertService.success('Equipment Deleted/Archived');
    })
  ), { dispatch: false });

  unarchiveEquipment = createEffect(() => this.actions$.pipe(
    ofType(unarchiveEquipment),
    switchMap(action => {
      return this.equipmentService.unarchiveEquipment(action.equipment).pipe(
        map(equipment => unarchiveEquipmentSuccess()),
        catchError(err => {
          this.store.dispatch(unarchiveEquipmentFail({ err }));
          return of(errorHappened({ err }));
        }));
    })
  ));

  unarchiveEquipmentSuccess = createEffect(() => this.actions$.pipe(
    ofType(unarchiveEquipmentSuccess),
    tap(action => {
      this.alertService.success('Equipment Unarchived');
    })
  ), { dispatch: false });

  getMyEquipmentList = createEffect(() => this.actions$.pipe(
    ofType(getMyEquipmentList),
    switchMap(() => this.store.select(s => s.accountState.user?.id).pipe(
      take(1),
      switchMap(userId => this.manageAssetService.getAssignedAssets({
        assignedToUserID: userId,
        first: 0,
        rows: 500
      }).pipe(
        map(result => getMyEquipmentListSuccess({
          equipments: result.result.map(x => this.mapAssignmentSummaryToAssignedEquipment(x))
        })),
        catchError(err => {
          this.store.dispatch(getMyEquipmentListFail({ err }));
          return of(errorHappened({ err }));
        })
      ))
    ))
  ));

  getAssignedEquipmentList = createEffect(() => this.actions$.pipe(
    ofType(getAssignedEquipmentList),
    switchMap(action => {
      return this.manageAssetService.getAssignedAssets(this.buildAssignedAssetsRequest(action.filter)).pipe(
        map(result => getAssignedEquipmentListSuccess({ equipmentWithAssignmentList: this.mapAssignedAssetsResults(result) })),
        catchError(err => {
          return of(errorHappened({ err }));
        }));
    })
  ));

  // get data for complete excel sheet
  getAssignedEquipments = createEffect(() => this.actions$.pipe(
    ofType(getAssignedEquipments),
    switchMap(action => {
      const request = this.buildAssignedAssetsRequest(action.filter);
      request.first = 0;
      request.rows = 5000;

      return this.manageAssetService.getAssignedAssets(request).pipe(
        map(result => getAssignedEquipmentsSuccess({ equipmentWithAssignment: this.mapAssignedAssetsResults(result) })),
        catchError(err => {
          return of(errorHappened({ err }));
        }));
    })
  ));

  searchEquipment = createEffect(() => this.actions$.pipe(
    ofType(searchEquipment),
    switchMap(action => {
      return this.manageAssetService.searchAvailableAssets({
        searchText: action.filter?.searchText,
        inventoryAssetLocationID: action.filter?.inventoryAssetLocationID ?? undefined,
        groupFilter: this.normalizeGroupFilter(action.filter?.groupFilter),
        includeTrackedItems: true,
        includeInventoryAssets: true
      }).pipe(
        map(results => searchEquipmentSuccess({
          equipments: {
            result: results.map(x => this.mapSearchResultToEquipment(x)),
            totalRecords: results.length
          }
        })),
        catchError(err => {
          this.store.dispatch(searchEquipmentFail({ err }));
          return of(errorHappened({ err }));
        }));
    })
  ));

  saveAssignEquipment = createEffect(() => this.actions$.pipe(
    ofType(saveAssignEquipment),
    switchMap(action => {
      return this.manageAssetService.assignAssets(action.assigningEquipments.map(x => ({
        assetInventoryID: x.assetInventoryID ?? x.equipment?.assetInventoryID ?? x.equipmentID,
        assetItemID: x.assetItemID ?? x.equipment?.assetItemID,
        assignedToUserID: x.userID ?? undefined,
        assignedToProgramID: x.assignedProgramID ?? undefined,
        returnDate: x.returnDateString ? new Date(x.returnDateString) : (x.returnDate ?? undefined),
        note: x.returnNotes ?? undefined
      }))).pipe(
        map(() => saveAssignEquipmentSuccess()),
        catchError(err => {
          this.store.dispatch(saveAssignEquipmentFail({ err }));
          return of(errorHappened({ err }));
        }));
    })
  ));

  saveAssignEquipmentSuccess = createEffect(() => this.actions$.pipe(
    ofType(saveAssignEquipmentSuccess),
    tap(action => {
      this.alertService.success('Asset assignment saved.', true);
    })
  ), { dispatch: false });

  saveEditAssignEquipment = createEffect(() => this.actions$.pipe(
    ofType(saveEditAssignEquipment),
    switchMap(action => {
      return this.equipmentService.editAssignEquipments(action.assigningEquipments).pipe(
        map(equipments => saveEditAssignEquipmentSuccess()),
        catchError(err => {
          this.store.dispatch(saveEditAssignEquipmentFail({ err }));
          return of(errorHappened({ err }));
        }));
    })
  ));

  saveEditAssignEquipmentSuccess = createEffect(() => this.actions$.pipe(
    ofType(saveEditAssignEquipmentSuccess),
    tap(action => {
      this.alertService.success('Assignement saved.', true);
    })
  ), { dispatch: false });

  getEquipmentAssignPdf = createEffect(() => this.actions$.pipe(
    ofType(getEquipmentAssignPdf),
    switchMap(action => this.manageAssetService.getAssignmentDocument(action.assignedEquipmentID, action.returned).pipe(
      map(doc => getEquipmentAssignPdfSuccess({ doc, download: action.download })),
      catchError(err => of(errorHappened({ err })))
    ))
  ));

  getEquipmentAssignPdfSuccess = createEffect(() => this.actions$.pipe(
    ofType(getEquipmentAssignPdfSuccess),
    tap(action => {
      if (action.download) {
        downloadFile(action.doc, 'AssetDocument.pdf');
      }
    })
  ), { dispatch: false });

  loadUserEquipments = createEffect(() => this.actions$.pipe(
    ofType(loadUserEquipments),
    switchMap(action => {
      return this.manageAssetService.getAssignedAssets({
        assignedToUserID: action.userID,
        assignedToProgramID: action.assignedProgramID,
        first: 0,
        rows: 500
      }).pipe(
        map(result => loadUserEquipmentsSuccess({
          assignedEquipments: result.result.map(x => this.mapAssignmentSummaryToAssignedEquipment(x))
        })),
        catchError(err => {
          this.store.dispatch(loadUserEquipmentsFail({ err }));
          return of(errorHappened({ err }));
        }));
    })
  ));

  loadUserEquipmentsSuccess = createEffect(() => this.actions$.pipe(
    ofType(loadUserEquipmentsSuccess),
    tap(action => {
    })
  ), { dispatch: false });

  saveReturnEquipment = createEffect(() => this.actions$.pipe(
    ofType(saveReturnEquipment),
    switchMap(action => {
      const items = action.returningEquipments.map(x => ({
        assetAssignedID: x.assetAssignedID ?? x.id,
        assetLocationID: x.assetLocationID,
        returnNote: x.returnNotes,
        assetReturnStatusID: x.assetReturnStatusID ?? undefined,
        assetItemConditionID: x.assetItemConditionID ?? undefined,
        dateReturned: x.dateReturnedString ? new Date(x.dateReturnedString) : (x.dateReturned ?? undefined),
        reassignToUserID: x.reassignToUserID ?? undefined,
        reassignToProgramID: x.reassignToProgramID ?? undefined,
        reassignReturnDate: x.reassignReturnDate ?? undefined,
        reassignNote: x.reassignNote ?? undefined
      }));
      const isReassign = action.isReassign === true
        || items.some(x => !!x.reassignToUserID || !!x.reassignToProgramID);
      const request = { items };
      const request$ = isReassign
        ? this.manageAssetService.reassignAssets(request)
        : this.manageAssetService.returnAssets(request);

      return request$.pipe(
        map(results => saveReturnEquipmentSuccess({
          assignedEquipments: results.map(result => this.mapAssignmentSummaryToAssignedEquipment(result)),
          wasReassign: isReassign
        })),
        catchError(err => {
          this.store.dispatch(saveReturnEquipmentFail({ err }));
          return of(errorHappened({ err }));
        }));
    })
  ));

  saveReturnEquipmentSuccess = createEffect(() => this.actions$.pipe(
    ofType(saveReturnEquipmentSuccess),
    tap(action => {
      if (action.wasReassign) {
        this.alertService.success(action.assignedEquipments?.length > 1 ? 'Assets re-assigned.' : 'Asset re-assigned.', true);
        return;
      }

      this.alertService.success(action.assignedEquipments?.length > 1 ? 'Asset returns saved.' : 'Asset return saved.', true);
    })
  ), { dispatch: false });

  signEquipmentAssign = createEffect(() => this.actions$.pipe(
    ofType(signEquipmentAssign),
    switchMap(action => this.manageAssetService.signAssignmentDocument(action.assignedEquipmentID, action.signedFields).pipe(
      map(docSigned => signEquipmentAssignSuccess({ docSigned })),
      catchError(err => of(signEquipmentAssignFail({ err }), errorHappened({ err })))
    ))
  ));

  private buildAssignedAssetsRequest(filter: any): ManageAssetAssignmentsRequestModel {
    const rows = filter?.rows ?? 25;
    const first = filter?.first ?? 0;
    const rawSearchText = [
      filter?.globalFilter
    ].find((value: string) => !!value && `${value}`.trim().length > 0);

    return {
      assignedToUserID: filter?.filterUserId > 0 ? filter.filterUserId : undefined,
      assetTypeID: filter?.filterTypeId > 0 ? filter.filterTypeId : undefined,
      searchText: rawSearchText ? `${rawSearchText}`.trim() : undefined,
      assignedToUserName: filter?.filterAssignedToUserName ? `${filter.filterAssignedToUserName}`.trim() : undefined,
      assetCategory: filter?.filterAssetCategory ? `${filter.filterAssetCategory}`.trim() : undefined,
      description: filter?.filterDescription ? `${filter.filterDescription}`.trim() : undefined,
      serialNumber: filter?.filterSerial ? `${filter.filterSerial}`.trim() : undefined,
      barcode: filter?.filterBarcode ? `${filter.filterBarcode}`.trim() : undefined,
      inventoryAssetLocation: filter?.filterLocation ? `${filter.filterLocation}`.trim() : undefined,
      returnDate: filter?.filterReturnDate ? new Date(filter.filterReturnDate) : undefined,
      assignmentState: filter?.filterAssignmentState ? `${filter.filterAssignmentState}`.trim() : 'assigned',
      groupFilter: this.normalizeGroupFilter(filter?.filterGroup),
      first,
      rows,
      sortField: this.mapSortField(filter?.sortField),
      sortOrder: typeof filter?.sortOrder === 'number' ? filter.sortOrder : -1
    };
  }

  private normalizeGroupFilter(groupFilter: unknown): string | undefined {
    const trimmed = `${groupFilter ?? ''}`.trim();
    return trimmed.length > 0 ? trimmed : undefined;
  }

  private mapSortField(sortField: string): string {
    switch ((sortField ?? '').trim()) {
      case 'equipment.type':
      case 'assetType':
        return 'assetType';
      case 'equipment.assetCategory':
      case 'assetCategory':
        return 'assetCategory';
      case 'equipment.description':
      case 'equipment.serialNumber':
        return 'description';
      case 'description':
        return 'description';
      case 'serialNumber':
        return 'serialNumber';
      case 'equipment.barcode':
      case 'barcode':
        return 'barcode';
      case 'lastAssignedEquipment.inventoryAssetLocation':
      case 'equipment.inventoryAssetLocation':
      case 'inventoryAssetLocation':
        return 'inventoryAssetLocation';
      case 'lastAssignedEquipment.userName':
      case 'assignedToUserName':
        return 'assignedToUserName';
      case 'isGrouped':
      case 'equipment.isGrouped':
      case 'lastAssignedEquipment.isGrouped':
        return 'assetGroup';
      case 'lastAssignedEquipment.returnDate':
      case 'returnDate':
        return 'returnDate';
      case 'lastAssignedEquipment.dateReturned':
      case 'dateReturned':
        return 'dateReturned';
      case 'lastAssignedEquipment.assetStatus':
      case 'assetStatus':
        return 'assetStatus';
      case 'lastAssignedEquipment.assignSigned':
      case 'assignSigned':
        return 'assignSigned';
      case 'lastAssignedEquipment.returnSigned':
      case 'returnSigned':
        return 'returnSigned';
      case 'lastAssignedEquipment.dateAssigned':
      case 'dateAssigned':
      default:
        return 'dateAssigned';
    }
  }

  private mapAssignedAssetsResults(result: { result: ManageAssetAssignmentSummaryModel[]; totalRecords: number; }): { result: EquipmentWithAssignmentsModel[]; totalRecords: number; } {
    return {
      result: (result?.result ?? []).map(x => this.mapAssignmentSummaryToEquipmentWithAssignments(x)),
      totalRecords: result?.totalRecords ?? 0
    };
  }

  private mapAssignmentSummaryToEquipmentWithAssignments(assignment: ManageAssetAssignmentSummaryModel): EquipmentWithAssignmentsModel {
    const mappedAssignment = this.mapAssignmentSummaryToAssignedEquipment(assignment);
    return {
      equipment: mappedAssignment.equipment,
      equipmentAssignments: [mappedAssignment],
      lastAssignedEquipment: mappedAssignment
    };
  }

  private mapAssignmentSummaryToAssignedEquipment(assignment: ManageAssetAssignmentSummaryModel): AssignedEquipmentModel {
    const equipment = this.mapAssignmentEquipment(assignment);
    const raw = assignment as any;

    return {
      id: assignment.assetAssignedID,
      assetAssignedID: assignment.assetAssignedID,
      equipmentID: assignment.assetAssignedID,
      assetInventoryID: assignment.assetInventoryID,
      assetItemID: assignment.assetItemID,
      isInventoryAssignment: assignment.isInventoryAssignment,
      userID: assignment.assignedToUserID,
      assignedToUserName: assignment.assignedToUserName,
      assignedProgramID: assignment.assignedToProgramID,
      dateAssigned: this.pickIsoDate(raw.dateAssigned ?? raw.DateAssigned),
      returnDate: this.pickIsoDate(raw.returnDate ?? raw.ReturnDate),
      dateReturned: this.pickIsoDate(raw.dateReturned ?? raw.DateReturned),
      returnedToUserID: raw.returnedByUserID ?? raw.ReturnedByUserID,
      assetReturnStatusID: raw.assetReturnStatusID ?? raw.AssetReturnStatusID,
      requireItemCondition: !!(raw.requireItemCondition ?? raw.RequireItemCondition),
      assetItemConditionID: raw.assetItemConditionID ?? raw.AssetItemConditionID,
      originalAssetItemConditionID: raw.assetItemConditionID ?? raw.AssetItemConditionID ?? null,
      programName: assignment.assignedToProgramName,
      equipment,
      description: assignment.description,
      assetCategory: assignment.assetCategory,
      barcode: assignment.barcode,
      inventoryAssetLocation: assignment.inventoryAssetLocation,
      assetStatus: assignment.assetStatus,
      assignDocumentToSignID: raw.assignDocumentToSignID ?? raw.AssignDocumentToSignID,
      returnDocumentToSignID: raw.returnDocumentToSignID ?? raw.ReturnDocumentToSignID,
      assignSigned: !!(raw.assignSigned ?? raw.AssignSigned),
      returnSigned: !!(raw.returnSigned ?? raw.ReturnSigned),
      historicalReturnSigned: !!(raw.historicalReturnSigned ?? raw.HistoricalReturnSigned),
      returnStatus: raw.assetReturnStatus ?? raw.AssetReturnStatus,
      returnNotes: raw.returnNotes ?? raw.ReturnNotes
    };
  }

  private pickIsoDate(value: unknown): Date | undefined {
    if (value == null || value === '') {
      return undefined;
    }
    const d = value instanceof Date ? value : new Date(value as string);
    return Number.isNaN(d.getTime()) ? undefined : d;
  }

  private mapSearchResultToEquipment(asset: ManageAssetSearchResultModel): EquipmentModel {
    const labelParts = [
      asset.assetType,
      asset.assetCategory,
      asset.description,
      asset.serialNumber,
      asset.barcode
    ].filter(x => !!x);

    return {
      id: asset.assetItemID ?? asset.assetInventoryID,
      assetInventoryID: asset.assetInventoryID,
      assetItemID: asset.assetItemID,
      isInventoryAssignment: asset.isInventoryAssignment,
      assetTypeID: asset.assetTypeID,
      assetCategory: asset.assetCategory,
      description: asset.description,
      barcode: asset.barcode,
      inventoryAssetLocation: asset.inventoryAssetLocation,
      inventoryAssetLocationID: asset.inventoryAssetLocationID,
      programName: asset.programName,
      quantityAvailable: asset.quantityAvailable,
      requireIndividualTracking: asset.requireIndividualTracking,
      canSelect: asset.canSelect,
      unavailableReason: asset.unavailableReason,
      isGrouped: asset.isGrouped,
      groupMemberCount: asset.groupMemberCount,
      assetGroupIDs: asset.assetGroupIDs ?? [],
      assetGroups: asset.assetGroups ?? [],
      serialNumber: asset.serialNumber ?? '',
      brand: asset.assetCategory ?? '',
      model: asset.description ?? '',
      serviceTag: '',
      expressServiceCode: '',
      type: asset.assetType,
      purchaseDate: null,
      assetTag: asset.barcode ?? '',
      label: labelParts.join(' - '),
      archived: false,
      department: '',
      pricing: '',
      purchaseVendor: '',
      purchaseLocation: '',
      storedLocation: asset.inventoryAssetLocation ?? '',
      storedLocationID: asset.inventoryAssetLocationID,
      storedOther: '',
      paymentMethod: '',
      size: '',
      classCode: '',
      classification: '',
      classificationOther: '',
      mainProgramID: 0,
      mainProgramName: asset.programName ?? '',
      equipmentTypeID: asset.assetTypeID
    };
  }

  private mapAssignmentEquipment(assignment: ManageAssetAssignmentSummaryModel): EquipmentModel {
    return {
      id: assignment.assetItemID ?? assignment.assetInventoryID,
      assetInventoryID: assignment.assetInventoryID,
      assetItemID: assignment.assetItemID,
      isInventoryAssignment: assignment.isInventoryAssignment,
      assetTypeID: assignment.assetTypeID,
      assetCategory: assignment.assetCategory,
      description: assignment.description,
      barcode: assignment.barcode,
      inventoryAssetLocation: assignment.inventoryAssetLocation,
      programName: assignment.programName,
      isGrouped: assignment.isGrouped,
      groupMemberCount: assignment.groupMemberCount,
      assetGroupIDs: assignment.assetGroupIDs ?? [],
      assetGroups: assignment.assetGroups ?? [],
      serialNumber: assignment.serialNumber ?? '',
      brand: assignment.assetCategory ?? '',
      model: assignment.description ?? '',
      serviceTag: '',
      expressServiceCode: '',
      type: assignment.assetType,
      purchaseDate: null,
      assetTag: assignment.barcode ?? '',
      label: [assignment.assetType, assignment.assetCategory, assignment.description, assignment.barcode].filter(x => !!x).join(' - '),
      archived: false,
      department: '',
      pricing: '',
      purchaseVendor: '',
      purchaseLocation: '',
      storedLocation: assignment.inventoryAssetLocation ?? '',
      storedLocationID: 0,
      storedOther: '',
      paymentMethod: '',
      size: '',
      classCode: '',
      classification: '',
      classificationOther: '',
      mainProgramID: 0,
      mainProgramName: assignment.programName ?? '',
      equipmentTypeID: assignment.assetTypeID
    };
  }

}
